import {
    MarkerType
} from "reactflow";
import { PolicyDecisionActionTypes } from "@iddataweb/idw-common-ui/enums";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { APPROVED_COLOR, DENIED_COLOR, OBLIGATION_COLOR } from "@constants/colors";
import { PolicyDispositionTypes } from "@enums/policy-disposition-types";
import { PolicyTypes } from "@enums/policy-types";
import { WorkflowVisualizationPolicyConfigEdge, WorkflowVisualizationPolicyConfigNode } from "@idw-types/common/workflow-visualization";
import {
    FormattedRpServiceConfiguration
} from "@idw-types/data/rp-service-configuration";
import { getPolicyTypeById } from "@utils/policies/policy-util";

export const getPolicyConfigNodes = (
    rpServices: Array<Partial<FormattedRpServiceConfiguration>>,
    primaryRpServiceId: string
): Array<WorkflowVisualizationPolicyConfigNode> => {
    const nodes: Array<WorkflowVisualizationPolicyConfigNode> = [];

    rpServices.forEach((service, index) => {
        if (service.policyConfig) {
            //add node for each service
            nodes.push({
                id: service.id,
                data: {
                    label: service.serviceName,
                    description: null,
                    apiKey: service.apiKey,
                    serviceId: service.id,
                    isPrimary: service.id === primaryRpServiceId
                },
                position: { x: index * 200, y: index * 100 },
                type: "workflowVisualizationServiceNode"
            });

            const hasApprove = service.policyConfig.defaultAction === PolicyDecisionActionTypes.APPROVE || service.policyConfig.decisions.some(decision => decision.policyDecisionType === PolicyDecisionActionTypes.APPROVE);
            if (hasApprove) {
                //if there is a default action of approve or one of the decisions resolves to approve
                //add an "approve" node
                nodes.push({
                    id: `${service.id}-pass`,
                    data: {
                        label: "Pass",
                        description: null,
                        apiKey: `${service.apiKey}-pass`,
                        serviceId: `${service.id}-pass`,
                        isPrimary: false
                    },
                    position: { x: index * 200, y: index * 100 },
                    type: "workflowVisualizationServiceNode"
                });
            }

            const hasDeny = service.policyConfig.defaultAction === PolicyDecisionActionTypes.DENY || service.policyConfig.decisions.some(decision => decision.policyDecisionType === PolicyDecisionActionTypes.DENY);
            if (hasDeny) {
                //if there is a default action of deny or one of the decisions resolves to deny
                //add a "deny" node
                nodes.push({
                    id: `${service.id}-fail`,
                    data: {
                        label: "Fail",
                        description: null,
                        apiKey: `${service.apiKey}-fail`,
                        serviceId: `${service.id}-fail`,
                        isPrimary: false
                    },
                    position: { x: index * 200, y: index * 100 },
                    type: "workflowVisualizationServiceNode"
                });
            }
        } else {
            //cant find a policy, so we just plot the service in case a decision points to it
            nodes.push({
                id: service.id,
                data: {
                    label: service.serviceName,
                    description: null,
                    apiKey: service.apiKey,
                    serviceId: service.id,
                    isPrimary: service.id === primaryRpServiceId
                },
                position: { x: index * 200, y: index * 100 },
                type: "workflowVisualizationServiceNode"
            });
        }
    });

    return nodes;
};

export const getPolicyConfigEdges = (
    rpServices: Array<Partial<FormattedRpServiceConfiguration>>,
    policyConfigNodes: Array<WorkflowVisualizationPolicyConfigNode>,
    // selectedPolicyConfigDecisions: Array<FormattedRpServiceConfigurationPolicyConfigDecision>
): Array<WorkflowVisualizationPolicyConfigEdge> => {
    const edges: Array<WorkflowVisualizationPolicyConfigEdge> = [];
    policyConfigNodes.forEach(node => {
        const service = rpServices.find(s => s.id === node.id);
        if (service?.policyConfig) {
            if (service.policyConfig.defaultAction === PolicyDecisionActionTypes.OBLIGATION) {
                //if default action is obligation, plot an edge to the step up api key
                const obligationToNode = policyConfigNodes.find(pcn => pcn.data.apiKey === service.policyConfig.defaultActionStepUpApiKey)
                if (obligationToNode) {
                    edges.push({
                        id: `${service.policyConfig.id}-default-obligation-action`,
                        source: `${service.id}`,
                        target: `${obligationToNode.id}`,
                        data: {
                            label: "Obligation",
                            description: "Obligation",
                            sourceServiceId: `${service.id}`,
                            isDefaultAction: true,
                            decisionUuids: []
                        },
                        type: "workflowVisualizationServiceEdge",
                        markerEnd: {
                            type: MarkerType.ArrowClosed,
                            width: 20,
                            height: 20,
                            color: OBLIGATION_COLOR,
                        },
                        style: {
                            strokeWidth: 1,
                            stroke: OBLIGATION_COLOR,
                        }
                    });
                }
            }

            const hasApprove = service.policyConfig.defaultAction === PolicyDecisionActionTypes.APPROVE || service.policyConfig.decisions.some(decision => decision.policyDecisionType === PolicyDecisionActionTypes.APPROVE);

            //if default action is approve or one of the decisions resolves to approve,
            //plot an edge to the approve node that was created
            if (hasApprove) {
                const approveDecisionUuids = service.policyConfig.decisions
                    .filter(decision => decision.policyDecisionType === PolicyDecisionActionTypes.APPROVE)
                    .map(decision => decision.uuid);

                edges.push({
                    id: `${service.policyConfig.id}-approve-action`,
                    source: `${service.id}`,
                    target: `${service.id}-pass`,
                    data: {
                        label: "Approve",
                        description: "Approve",
                        sourceServiceId: `${service.id}`,
                        isDefaultAction: false,
                        decisionUuids: approveDecisionUuids
                    },
                    type: "workflowVisualizationServiceEdge",
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        width: 20,
                        height: 20,
                        color: APPROVED_COLOR,
                    },
                    style: {
                        strokeWidth: 1,
                        stroke: APPROVED_COLOR,
                    }
                });
            }

            //if default action is deny or one of the decisions resolves to deny,
            //plot an edge to the deny node that was created
            const hasDeny = service.policyConfig.defaultAction === PolicyDecisionActionTypes.DENY || service.policyConfig.decisions.some(decision => decision.policyDecisionType === PolicyDecisionActionTypes.DENY);
            if (hasDeny) {
                const denyDecisionUuids = service.policyConfig.decisions
                    .filter(decision => decision.policyDecisionType === PolicyDecisionActionTypes.DENY)
                    .map(decision => decision.uuid);


                edges.push({
                    id: `${service.policyConfig.id}-deny-action`,
                    source: `${service.id}`,
                    target: `${service.id}-fail`,
                    data: {
                        label: "Deny",
                        description: "Deny",
                        sourceServiceId: `${service.id}`,
                        isDefaultAction: true,
                        decisionUuids: denyDecisionUuids
                    },
                    type: "workflowVisualizationServiceEdge",
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        width: 20,
                        height: 20,
                        color: DENIED_COLOR,
                    },
                    style: {
                        strokeWidth: 1,
                        stroke: DENIED_COLOR,
                    }
                });
            }

            //for all decisions that are obligations, plot an edge to the
            //step up api keys
            service.policyConfig.decisions.filter(decision => decision.policyDecisionType === PolicyDecisionActionTypes.OBLIGATION).forEach(obligationDecision => {
                const obligationToNode = policyConfigNodes.find(pcn => pcn.data.apiKey === obligationDecision.obligationValue);
                if (obligationToNode) {
                    const dispositionText = obligationDecision.disposition === PolicyDispositionTypes.POSITIVE ? "Pass" : "Fail";
                    edges.push({
                        id: `${obligationDecision.id}`,
                        source: `${service.id}`,
                        target: `${obligationToNode.id}`,
                        data: {
                            label: dispositionText,
                            description: "Obligation", //obligationDecision.description
                            sourceServiceId: `${service.id}`,
                            isDefaultAction: false,
                            decisionUuids: [obligationDecision.uuid]
                        },
                        type: "workflowVisualizationServiceEdge",
                        markerEnd: {
                            type: MarkerType.ArrowClosed,
                            width: 20,
                            height: 20,
                            color: OBLIGATION_COLOR,
                        },
                        style: {
                            strokeWidth: 1,
                            stroke: OBLIGATION_COLOR,
                            width: "fit-content"
                        }
                    });
                }
            });
        } else if ((service?.policies || []).length > 0) {
            const usePolicyManager = isStringTrue(service.usePolicyManager);
            const policyType = getPolicyTypeById(usePolicyManager, service.policyConfig, service.policies);
            
            if (policyType === PolicyTypes.ALWAYS_OBLIGATE) {
                let stepUpApiKey: string = null;
                const policy = service.policies[0];
                if (policy.properties && policy.properties.length > 0) {
                    const property = policy.properties.find(p => (p.name || "").toLowerCase() === "obligation.apikey");
                    if (property?.value) {
                        stepUpApiKey = property.value as string;
                    }
                }

                const obligationToNode = policyConfigNodes.find(pcn => pcn.data.apiKey === stepUpApiKey)
                if (obligationToNode) {
                    edges.push({
                        id: `${service.policies[0].id}-default-obligation-action`,
                        source: `${service.id}`,
                        target: `${obligationToNode.id}`,
                        data: {
                            label: "Obligation",
                            description: "Obligation",
                            sourceServiceId: `${service.id}`,
                            isDefaultAction: true,
                            decisionUuids: []
                        },
                        type: "workflowVisualizationServiceEdge",
                        markerEnd: {
                            type: MarkerType.ArrowClosed,
                            width: 20,
                            height: 20,
                            color: OBLIGATION_COLOR,
                        },
                        style: {
                            strokeWidth: 1,
                            stroke: OBLIGATION_COLOR,
                        }
                    });
                }
            }
            //TODO - maybe add always approve/deny stuff
        }
    });

    return edges;
};